import React, { useState, useRef, lazy } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { InstagramIcon, ExternalLinkIcon, SmartphoneIcon, ChevronDownIcon, StarIcon, YoutubeIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { TikTokIcon, SpotifyIcon, XLogoIcon, CustomFacebookIcon as FacebookIcon } from './shared/Icons';
import { CATEGORIES, accounts, featuredCreators, SocialCategory, SocialAccount } from '../data/social-accounts';
function FeaturedSpotlight() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  if (featuredCreators.length === 0) return null;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 0.3
  }} className="max-w-5xl mx-auto mb-6" data-id="element-3470" data-mp="66huzkz9p1b5">
      <div className="rounded-2xl border-l-4 border-amber-400 bg-white shadow-md overflow-hidden" data-id="element-3471" data-mp="2anseendar6tl">
        {/* Accordion header button */}
        <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="flex items-center justify-between px-5 py-4 text-left hover:bg-amber-50/50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-id="element-3472" data-mp="2fmvju8x6vf23">
          <div className="flex items-center gap-3" data-id="element-3473" data-mp="2tllgjng3jcpx">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-3474" data-mp="2ykpq4f6xncgs">
              <StarIcon className="w-5 h-5 text-white fill-white" data-id="element-3475" data-mp="33e118sis2fei" />
            </div>
            <span className="font-bebas text-2xl tracking-wider text-amber-600" data-id="element-3476" data-mp="38ahqxr5ml1tp">
              {isEs ? 'Destacados: Artistas, Narradores y Agentes de Cambio' : 'Featured: Artists, Storytellers & Changemakers'}
            </span>
            <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-amber-50 px-2 py-1 rounded-full" data-id="element-3477" data-mp="1i6fpbxywy915">
              {featuredCreators.length}{' '}
              {isEs ? featuredCreators.length === 1 ? 'creador' : 'creadores' : featuredCreators.length === 1 ? 'creator' : 'creators'}
            </span>
          </div>
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex-shrink-0 ml-2" data-id="element-3478" data-mp="20vvuiyljmpnw">
            <ChevronDownIcon className="w-5 h-5 text-amber-500" data-id="element-3479" data-mp="25wa5opifyfq5" />
          </motion.div>
        </button>

        {/* Collapsible content */}
        <AnimatePresence initial={false} data-id="element-3480" data-mp="fkdwyte4s77y">
          {isOpen && <motion.div key="featured-content" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="overflow-hidden" data-id="element-3481" data-mp="bjk8nyws0qej">
              <div className="border-t border-amber-100 px-5 py-5 bg-amber-50/30" data-id="element-3482" data-mp="3dunyxkrf4uuq">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3483" data-mp="3iwn2ovr2lts7">
                  {featuredCreators.map((creator, i) => {
                const hasLinks = creator.instagramUrl || creator.tiktokUrl || creator.youtubeUrl || creator.spotifyUrl || creator.xUrl || creator.facebookUrl;
                return <motion.div key={creator.handle} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4,
                  delay: i * 0.08
                }} className="relative bg-white rounded-2xl shadow-sm border-2 border-amber-200 overflow-hidden flex flex-col" data-id="element-3484" data-mp="346f8yw90v24a" data-mpi={i}>
                        {/* Gold accent stripe */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400" data-id="element-3485" data-mp="26j6hmkmcayud" />

                        {/* Creator info header */}
                        <div className="p-4 pt-5" data-id="element-3486" data-mp="1xh6ztq3i3qqd">
                          <div className="flex items-center gap-3 mb-2" data-id="element-3487" data-mp="1xjtgcf8ddgkq">
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${creator.imageGradient} flex items-center justify-center flex-shrink-0 shadow-sm text-white font-bebas text-lg tracking-wider ring-2 ring-amber-300 ring-offset-1`} data-id="element-3488" data-mp="cywfvva6lwnm">
                              {creator.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3489" data-mp="3qfvnk3iiync3">
                              <h3 className="font-bebas text-lg text-haa-navy tracking-wider leading-none truncate" data-id="element-3490" data-mp="3ufwcwj75wixx">
                                {creator.name}
                              </h3>
                              <p className="font-nunito text-[11px] font-bold text-gray-500 truncate" data-id="element-3491" data-mp="80bgtz6o1j0r">
                                {creator.handle}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5" data-id="element-3492" data-mp="3bnv8l1bmc0h4">
                            <StarIcon className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0 mt-0.5" data-id="element-3493" data-mp="itd2zuuzs5m8" />
                            <p className="font-nunito text-[11px] text-amber-800 leading-snug italic line-clamp-2" data-id="element-3494" data-mp="duabzebujzx9">
                              {isEs ? creator.featuredReasonEs : creator.featuredReason}
                            </p>
                          </div>
                        </div>

                        {/* Embed area */}
                        {creator.embedUrl ? <div className="flex-1 border-t border-amber-100 bg-gray-50/50 min-h-0" data-id="element-3495" data-mp="8y325agby4a1">
                            <iframe src={creator.embedUrl} className="w-full h-[380px]" frameBorder="0" scrolling="no" allowTransparency allow="encrypted-media" loading="lazy" referrerPolicy="strict-origin-when-cross-origin" title={`${creator.name} embed`} data-id="element-3496" data-mp="3wvudbe6t53d" />
                          </div> : <a href={creator.instagramUrl || '#'} target="_blank" rel="noopener noreferrer" className={`flex-1 border-t border-amber-100 bg-gradient-to-br ${creator.imageGradient} flex flex-col items-center justify-center p-6 min-h-[380px] text-white text-center group/embed transition-all duration-300 hover:brightness-110`} data-id="element-3497" data-mp="1ivjmanrszkgh">
                            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover/embed:scale-110 transition-transform duration-300" data-id="element-3498" data-mp="2pif44q5of94o">
                              <InstagramIcon className="w-8 h-8" data-id="element-3499" data-mp="u8yyhpaascxa" />
                            </div>
                            <p className="font-bebas text-3xl tracking-wider leading-none" data-id="element-3500" data-mp="pg5td0ax32ko">
                              {creator.name}
                            </p>
                            <p className="font-nunito text-sm text-white/90 mt-1" data-id="element-3501" data-mp="ggsl2ayj4k1j">
                              {creator.handle}
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-haa-navy font-nunito font-bold text-sm shadow-lg group-hover/embed:bg-white/95 transition-colors" data-id="element-3502" data-mp="1d604ndfl8j2t">
                              {isEs ? 'Ver en Instagram' : 'View on Instagram'}
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-3503" data-mp="z94kaotvz57g">
                                <path d="M7 17L17 7M7 7h10v10" data-id="element-3504" data-mp="28eby0l67ampj" />
                              </svg>
                            </span>
                          </a>}

                        {/* Platform links footer */}
                        {hasLinks && <div className="flex items-center justify-center gap-3 py-3 border-t border-amber-100" data-id="element-3505" data-mp="2n3hsxwy09qrm">
                            {creator.instagramUrl && <a href={creator.instagramUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 transition-colors" title="Instagram" data-id="element-3506" data-mp="2w5zgqftb6rgq">
                                <InstagramIcon className="w-4 h-4" data-id="element-3507" data-mp="t7pylm4xhtv5" />
                              </a>}
                            {creator.tiktokUrl && <a href={creator.tiktokUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors" title="TikTok" data-id="element-3508" data-mp="1365pbo01n144">
                                <TikTokIcon className="w-4 h-4" data-id="element-3509" data-mp="1lwnc7by732hk" />
                              </a>}
                            {creator.youtubeUrl && <a href={creator.youtubeUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title="YouTube" data-id="element-3510" data-mp="1qu6tylu3pxe9">
                                <YoutubeIcon className="w-4 h-4" data-id="element-3511" data-mp="36gsgaiuyoknc" />
                              </a>}
                            {creator.spotifyUrl && <a href={creator.spotifyUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors" title="Spotify" data-id="element-3512" data-mp="30bn0pveyi9e">
                                <SpotifyIcon className="w-4 h-4" data-id="element-3513" data-mp="lqtekr4if7t6" />
                              </a>}
                            {creator.xUrl && <a href={creator.xUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors" title="X" data-id="element-3514" data-mp="2b6ds8ds93zpk">
                                <XLogoIcon className="w-4 h-4" data-id="element-3515" data-mp="1sel1yr4kgslj" />
                              </a>}
                            {creator.facebookUrl && <a href={creator.facebookUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Facebook" data-id="element-3516" data-mp="1ihzrhq9s2pab">
                                <FacebookIcon className="w-4 h-4" data-id="element-3517" data-mp="1680fuso573us" />
                              </a>}
                          </div>}
                      </motion.div>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
function SocialCard({
  account,
  index
}: {
  account: SocialAccount;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const isInstagram = account.platform === 'Instagram';
  const isYouTube = account.platform === 'YouTube';
  const showBio = isHovered || isExpanded;
  return <motion.article layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} transition={{
    duration: 0.4,
    delay: index * 0.05,
    ease: [0.16, 1, 0.3, 1]
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => setIsExpanded(!isExpanded)} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300 flex flex-col cursor-pointer overflow-hidden h-fit" data-id="element-3518" data-mp="4i52wxvdubw4">
      <div className="p-4 flex items-center gap-3" data-id="element-3519" data-mp="28yruqs9kydta">
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${account.imageGradient} flex items-center justify-center flex-shrink-0 shadow-sm text-white font-bebas text-xl tracking-wider`} data-id="element-3520" data-mp="2d16a32e1ivft">
          {account.name.charAt(0)}
        </div>

        {/* Name & Handle */}
        <div className="flex-1 min-w-0" data-id="element-3521" data-mp="1pbkcukh8aqag">
          <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-none truncate" data-id="element-3522" data-mp="1paitlsq26xhn">
            {account.name}
          </h3>
          <p className="font-nunito text-xs font-bold text-gray-500 mt-1 truncate" data-id="element-3523" data-mp="1hpymohepan9x">
            {account.handle}
          </p>
        </div>

        {/* Platform Icon */}
        <div className={`p-2 rounded-full flex-shrink-0 ${isInstagram ? 'bg-pink-50 text-pink-600' : isYouTube ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-800'}`} data-id="element-3524" data-mp="18pthmdwzc0yx">
          {isInstagram ? <InstagramIcon className="w-4 h-4" data-id="element-3525" data-mp="yv9xb33fmlfv" /> : isYouTube ? <YoutubeIcon className="w-4 h-4" data-id="element-3526" data-mp="u0xcaomb5220" /> : <TikTokIcon className="w-4 h-4" data-id="element-3527" data-mp="p2vvkacctetp" />}
        </div>
      </div>

      {/* Expandable Bio */}
      <AnimatePresence data-id="element-3528" data-mp="l2lf23qxr60l">
        {showBio && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-3529" data-mp="kzpgqeb6efbm">
            <div className="px-4 pb-4 pt-1 border-t border-gray-50 mt-1" data-id="element-3530" data-mp="3no9yztecav1w">
              <p className="font-nunito text-gray-600 text-sm leading-relaxed mb-3" data-id="element-3531" data-mp="3sm1nedfrzz1c">
                {isEs ? account.bioEs : account.bio}
              </p>
              <a href={account.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center justify-center gap-1.5 w-full py-2 bg-gray-50 hover:bg-gray-100 text-haa-navy font-nunito font-bold text-xs rounded-lg transition-colors border border-gray-200" data-id="element-3532" data-mp="k79vz6zapal0">
                {isEs ? `Seguir en ${account.platform}` : `Follow on ${account.platform}`}
                <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-3533" data-mp="2sm7zp0hnat2m" />
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.article>;
}
function CategoryAccordion({
  category,
  accounts,
  index
}: {
  category: SocialCategory;
  accounts: SocialAccount[];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const [isOpen, setIsOpen] = useState(index === 0);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const Icon = category.icon;
  const isEmpty = accounts.length === 0;
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className={`rounded-2xl border-l-4 ${category.borderColor} bg-white shadow-md overflow-hidden mb-4`} data-id="element-3534" data-mp="2ipd52sohtctm">
      <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-3535" data-mp="1nddlovrn94wx">
        <div className="flex items-center gap-3" data-id="element-3536" data-mp="225xk5j3umo81">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-3537" data-mp="264o8p1u90xjj">
            <Icon className="w-5 h-5 text-white" data-id="element-3538" data-mp="2g45ctetur3de" />
          </div>
          <span className={`font-bebas text-2xl tracking-wider ${category.accentColor}`} data-id="element-3539" data-mp="2g1su2zg9soza">
            {isEs ? category.labelEs : category.label}
          </span>
          {isEmpty ? <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-gray-100 px-2 py-1 rounded-full" data-id="element-3540" data-mp="ls7ei7x2zrwa">
              {isEs ? 'Próximamente' : 'Coming soon'}
            </span> : <span className="font-nunito text-xs text-gray-400 font-semibold ml-2 bg-gray-100 px-2 py-1 rounded-full" data-id="element-3541" data-mp="2z8ilmm0r85i2">
              {accounts.length}{' '}
              {isEs ? accounts.length === 1 ? 'creador' : 'creadores' : accounts.length === 1 ? 'creator' : 'creators'}
            </span>}
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex-shrink-0 ml-2" data-id="element-3542" data-mp="3ivy9s2uq4034">
          <ChevronDownIcon className={`w-5 h-5 ${category.accentColor}`} data-id="element-3543" data-mp="38y24vssdq1dg" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-3544" data-mp="22g57vpw7mee7">
        {isOpen && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-3545" data-mp="1xidfyvvkesa0">
            <div className="border-t border-gray-100 px-5 py-5 bg-gray-50/50" data-id="element-3546" data-mp="2s5e6gy2k8g8o">
              {isEmpty ? <div className="text-center py-8" data-id="element-3547" data-mp="2n72wmnc9m5qg">
                  <p className="font-nunito text-gray-400 text-sm" data-id="element-3548" data-mp="2n47093g2fp44">
                    {isEs ? 'Creadores próximamente — ¡mantente atento!' : 'Creators coming soon — stay tuned!'}
                  </p>
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3549" data-mp="16poy8f7vmr11">
                  {accounts.map((account, i) => <SocialCard key={account.handle} account={account} index={i} data-id="element-3550" data-mp="3dgk4gldj06pd" data-mpi={i} />)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
type FilterType = 'All' | 'Todos' | 'Instagram' | 'TikTok' | 'YouTube' | 'A–Z';
export function SocialMediaVoices() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [filter, setFilter] = useState<FilterType>(isEs ? 'Todos' : 'All');
  const filters: FilterType[] = isEs ? ['Todos', 'Instagram', 'TikTok', 'YouTube', 'A–Z'] : ['All', 'Instagram', 'TikTok', 'YouTube', 'A–Z'];
  // Form State
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [youtube, setYoutube] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [facebook, setFacebook] = useState('');
  const [spotify, setSpotify] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const resetForm = () => {
    setName('');
    setInstagram('');
    setTiktok('');
    setYoutube('');
    setXHandle('');
    setFacebook('');
    setSpotify('');
    setPhone('');
    setEmail('');
    setCategory('');
    setDescription('');
    setSubmitted(false);
  };
  const INFLUENCER_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbzuw7-2_a9uswZei5vcWrgBvR1KxUY0lnuypozlyZvfkc9MNxJKlrw0NiDC7DQpHAj-/exec';
  const handleSubmit = () => {
    const data = {
      name,
      instagram,
      tiktok,
      youtube,
      x: xHandle,
      facebook,
      spotify,
      phone,
      email,
      category,
      description,
      timestamp: new Date().toISOString()
    };
    fetch(INFLUENCER_SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Creator Google Sheets submission error:', err);
    });
    setSubmitted(true);
  };
  // First filter by platform
  const platformFilteredAccounts = accounts.filter(account => filter === 'All' || filter === 'Todos' || filter === 'A–Z' || account.platform === filter);
  // For A–Z view, sort alphabetically by name
  const sortedAccounts = filter === 'A–Z' ? [...platformFilteredAccounts].sort((a, b) => a.name.localeCompare(b.name)) : platformFilteredAccounts;
  return <>
      <section id="social-voices" className="bg-gray-50/50 py-16 lg:py-24" aria-labelledby="social-voices-heading" data-id="element-3551" data-mp="3lueuwfrwnvzh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3552" data-mp="3bxjx5z3ghzm9">
          {/* Header */}
          <div ref={titleRef} className="text-center mb-12" data-id="element-3553" data-mp="37w84tlt770ad">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={titleInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} data-id="element-3554" data-mp="37unb1yepjldk">
              <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2" data-id="element-3555" data-mp="w7gtzr2xnm09">
                <SmartphoneIcon className="w-4 h-4" data-id="element-3556" data-mp="18ormg58v7k0s" />
                {isEs ? 'Comunidad Digital' : 'Digital Community'}
              </span>
              <h2 id="social-voices-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-3557" data-mp="1dgi1goiqyvyj">
                {isEs ? 'Voces Destacadas' : 'Uplifting Voices'}
              </h2>
              <div className="flex items-center justify-center gap-3 mt-4" data-id="element-3558" data-mp="prgocnyyywmx">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-3559" data-mp="uoyz8jwjrlxk" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-3560" data-mp="uruxk9cb4cmj" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-3561" data-mp="zq686mafer5t" />
              </div>
              <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-3562" data-mp="263vuibjbr7dd">
                {isEs ? 'Descubre y conecta con creadores, organizaciones y líderes hondureño-americanos notables que están generando un impacto en las plataformas de redes sociales.' : 'Discover and connect with notable Honduran-American creators, organizations, and leaders making an impact across social media platforms.'}
              </p>
              <div className="mt-5 max-w-2xl mx-auto bg-haa-skyblue/10 border border-haa-skyblue/30 rounded-xl px-5 py-4 text-sm font-nunito text-gray-700 leading-relaxed" data-id="element-3563" data-mp="g02b8pri8c3e">
                {isEs ? 'Este directorio tiene fines informativos. La inclusión no constituye un respaldo ni una promoción de ningún influencer, creador o su contenido en particular. Curamos esta lista para destacar voces hondureño-americanas notables en las redes sociales. Si tiene preguntas o inquietudes, comuníquese con nosotros — con gusto le ayudaremos.' : 'This directory is meant to be informational. Inclusion does not constitute an endorsement or promotion of any particular influencer, creator, or their content. We curate this list to highlight notable Honduran-American voices across social media.'}
              </div>
            </motion.div>
          </div>

          {/* Filters */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="flex justify-center mb-10" data-id="element-3564" data-mp="2gphoq0j17cyd">
            <div className="inline-flex flex-wrap justify-center gap-1 bg-white p-1.5 rounded-xl shadow-sm border border-gray-100" data-id="element-3565" data-mp="2zhae7kyw6g1c">
              {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`relative px-4 sm:px-6 py-2 text-xs sm:text-sm font-nunito font-bold rounded-lg transition-colors ${filter === f ? 'text-white' : 'text-gray-500 hover:text-haa-navy'}`} data-id="element-3566" data-mp="1nz9fiy6h42vx">
                  {filter === f && <motion.div layoutId="activeFilter" className="absolute inset-0 bg-haa-navy rounded-lg" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-3567" data-mp="10a5ct7esh5nm" />}
                  <span className="relative z-10" data-id="element-3568" data-mp="1o22p0zm5hzp9">{f}</span>
                </button>)}
            </div>
          </motion.div>

          {/* Featured Creator of the Month */}
          <FeaturedSpotlight data-id="element-3569" data-mp="1f7flk901rxfn" />

          {/* Category Accordions or A–Z Grid */}
          <div className="max-w-5xl mx-auto" data-id="element-3570" data-mp="2bqdkfn92v765">
            {filter === 'A–Z' ? <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3571" data-mp="26ylnmuzbw57k">
                <AnimatePresence mode="popLayout" data-id="element-3572" data-mp="3pnomlxgnayl3">
                  {sortedAccounts.map((account, i) => <SocialCard key={account.handle} account={account} index={i} data-id="element-3573" data-mp="3umjb9g1anhd4" data-mpi={i} />)}
                </AnimatePresence>
              </motion.div> : <>
                {CATEGORIES.map((category, i) => {
              const categoryAccounts = sortedAccounts.filter(a => a.categoryId === category.id);
              return <CategoryAccordion key={category.id} category={category} accounts={categoryAccounts} index={i} data-id="element-3574" data-mp="q6gs5nqzvk9k" data-mpi={i} />;
            })}
              </>}
          </div>

          {/* Footer CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.7
        }} className="mt-12 max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-haa-blue/5 border border-haa-blue/20 rounded-2xl p-6" data-id="element-3575" data-mp="20er0vju4xghb">
            <div className="flex items-start gap-4" data-id="element-3576" data-mp="2o77khytitgiq">
              <SmartphoneIcon className="w-6 h-6 text-haa-blue flex-shrink-0 mt-0.5" data-id="element-3577" data-mp="2acx4q6zi4wot" />
              <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-3578" data-mp="2fc9midbindkj">
                <span className="font-bold text-haa-navy block mb-1" data-id="element-3579" data-mp="332x8u3m6i5qm">
                  {isEs ? '¿Conoce a un creador o influencer hondureño-americano que deberíamos destacar?' : 'Know a Honduran-American creator or influencer we should feature?'}
                </span>
                {isEs ? 'Si usted es o conoce a un creador notable de redes sociales hondureño-americano, envíelo para consideración en nuestro directorio.' : 'If you are or know a notable Honduran-American social media creator, submit them for consideration in our directory.'}
              </p>
            </div>
            <button onClick={() => setModalOpen(true)} className="flex-shrink-0 w-full sm:w-auto py-2.5 px-5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors whitespace-nowrap" data-id="element-3580" data-mp="2y9l7m0egqqe4">
              {isEs ? 'Sugerir un Creador' : 'Submit a Creator'}
            </button>
          </motion.div>
        </div>
      </section>
      {/* Form Modal */}
      <AnimatePresence data-id="element-3581" data-mp="iifkk4f1balp">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => {
        setModalOpen(false);
        if (submitted) resetForm();
      }} data-id="element-3582" data-mp="ihnn0o1vn2ss">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-3583" data-mp="6st2nzd558mg">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-3584" data-mp="37r6g1z5o33lx">
                <div className="flex items-center gap-3" data-id="element-3585" data-mp="32s3jz47o07q3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-midblue flex items-center justify-center shadow-sm" data-id="element-3586" data-mp="32rk0vk6a3dah">
                    <SmartphoneIcon className="w-5 h-5 text-white" data-id="element-3587" data-mp="2u01lvcl2j3iz" />
                  </div>
                  <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider leading-none pt-1" data-id="element-3588" data-mp="2p0or85nygs5u">
                    {isEs ? 'Sugerir un Creador o Influencer' : 'Submit a Creator or Influencer'}
                  </h2>
                </div>
                <button onClick={() => {
              setModalOpen(false);
              if (submitted) resetForm();
            }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0" aria-label={isEs ? 'Cerrar' : 'Close'} data-id="element-3589" data-mp="2b0w5korad24q">
                  <XIcon className="w-5 h-5" data-id="element-3590" data-mp="2qx5ji184qa9j" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-3591" data-mp="2h125ny3x0n6r">
                {submitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center justify-center py-10 text-center" data-id="element-3592" data-mp="2m05bhkrpyxfs">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4" data-id="element-3593" data-mp="66wpugd2og71">
                      <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-3594" data-mp="1usn0kqvs5gek">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-3595" data-mp="24kjbx3mvn30q" />
                      </svg>
                    </div>
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-2" data-id="element-3596" data-mp="29icelmoz1ew9">
                      {isEs ? '¡Gracias por su Sugerencia!' : 'Thank You for Your Submission!'}
                    </h3>
                    <p className="font-nunito text-gray-600 text-sm max-w-md" data-id="element-3597" data-mp="2igepctyiblwq">
                      {isEs ? 'Hemos recibido la información del creador. Nuestro equipo la revisará para considerarla en nuestro directorio de Voces Destacadas.' : "We've received the creator information. Our team will review it for consideration in our Uplifting Voices directory."}
                    </p>
                  </motion.div> : <div className="space-y-5" data-id="element-3598" data-mp="2y6qc69cdzmro">
                    <p className="font-nunito text-gray-500 text-sm" data-id="element-3599" data-mp="326h80rgrst32">
                      {isEs ? 'Complete el formulario a continuación para sugerir un creador hondureño-americano notable. Todos los campos son opcionales.' : 'Fill out the form below to suggest a notable Honduran-American creator. All fields are optional.'}
                    </p>

                    <div data-id="element-3600" data-mp="3luhnpaq3rvrw">
                      <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3601" data-mp="3ltoqb0d1aimf">
                        {isEs ? 'Nombre / Nombre de la Cuenta' : 'Name / Account Name'}
                      </label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3602" data-mp="1j7t0vs88dyhu" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3603" data-mp="21wr197u4usl2">
                      <div data-id="element-3604" data-mp="1xw74449k7oyx">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3605" data-mp="1xwz0view7sqs">
                          <InstagramIcon className="w-4 h-4 text-pink-500" data-id="element-3606" data-mp="1t3mbv76axjxi" />
                          {isEs ? 'Usuario de Instagram' : 'Instagram Handle'}
                        </label>
                        <input type="text" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3607" data-mp="2lncl4mr8knat" />
                      </div>
                      <div data-id="element-3608" data-mp="1k6km7yohgqnb">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3609" data-mp="27yhlkqaqxq7j">
                          <TikTokIcon className="w-4 h-4 text-gray-800" data-id="element-3610" data-mp="2cw9muaxa6ooe" />
                          {isEs ? 'Usuario de TikTok' : 'TikTok Handle'}
                        </label>
                        <input type="text" value={tiktok} onChange={e => setTiktok(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3611" data-mp="22ylrpjvrjf5p" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3612" data-mp="txle5793fm12">
                      <div data-id="element-3613" data-mp="oz0izm8p7sjh">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3614" data-mp="p0v1ktwhz8ke">
                          <YoutubeIcon className="w-4 h-4 text-red-500" data-id="element-3615" data-mp="17v2r5yuiy8n" />
                          {isEs ? 'Canal de YouTube' : 'YouTube Channel'}
                        </label>
                        <input type="text" value={youtube} onChange={e => setYoutube(e.target.value)} placeholder={isEs ? '@canal o URL' : '@channel or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3616" data-mp="b1btg13orx83" />
                      </div>
                      <div data-id="element-3617" data-mp="2jzqicier3j71">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3618" data-mp="2jyykt21lfbe4">
                          <XLogoIcon className="w-4 h-4 text-gray-800" data-id="element-3619" data-mp="2eykmwctphhtt" />
                          {isEs ? 'Usuario de X / Twitter' : 'X / Twitter Handle'}
                        </label>
                        <input type="text" value={xHandle} onChange={e => setXHandle(e.target.value)} placeholder={isEs ? '@usuario' : '@username'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3620" data-mp="210vyiv0fjsk0" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3621" data-mp="3k0v8nrulbqva">
                      <div data-id="element-3622" data-mp="368x65zvdwt83">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3623" data-mp="369qg19pn3yug">
                          <FacebookIcon className="w-4 h-4 text-blue-600" data-id="element-3624" data-mp="1tnyhluzo9t0" />
                          Facebook
                        </label>
                        <input type="text" value={facebook} onChange={e => setFacebook(e.target.value)} placeholder={isEs ? 'Nombre de la página o URL' : 'Page name or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3625" data-mp="6rpiqa45if62" />
                      </div>
                      <div data-id="element-3626" data-mp="j5cfbih61eop">
                        <label className="flex items-center gap-1.5 font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3627" data-mp="ebroqbhwjmzj">
                          <SpotifyIcon className="w-4 h-4 text-green-500" data-id="element-3628" data-mp="a9mvhdvd8sdf" />
                          Spotify
                        </label>
                        <input type="text" value={spotify} onChange={e => setSpotify(e.target.value)} placeholder={isEs ? 'Nombre del artista o URL' : 'Artist name or URL'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3629" data-mp="598olz5i3ogz" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3630" data-mp="38kzz987il2uw">
                      <div data-id="element-3631" data-mp="3decc6l2tzl7t">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3632" data-mp="3de2wbqbi2ufu">
                          {isEs ? 'Teléfono' : 'Phone'}
                        </label>
                        <input type="tel" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3633" data-mp="2kq6s80jlq091" />
                      </div>
                      <div data-id="element-3634" data-mp="cgh19znk90op">
                        <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3635" data-mp="chs4nyd3g6fa">
                          {isEs ? 'Correo Electrónico' : 'Email'}
                        </label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isEs ? 'correo@ejemplo.com' : 'email@example.com'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm" data-id="element-3636" data-mp="lighwquny2r4" />
                      </div>
                    </div>

                    <div data-id="element-3637" data-mp="1e74az8lnlwf8">
                      <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3638" data-mp="1e49rbjagsfmv">
                        {isEs ? 'Categoría' : 'Category'}
                      </label>
                      <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm bg-white" data-id="element-3639" data-mp="1stujatxsopab">
                        <option value="" data-id="element-3640" data-mp="1a1ijoeyj023v">
                          {isEs ? '-- Seleccione --' : '-- Select --'}
                        </option>
                        {CATEGORIES.map(c => <option key={c.id} value={c.label} data-id="element-3641" data-mp="105f7eg9ymn37">
                            {isEs ? c.labelEs : c.label}
                          </option>)}
                        <option value="Other" data-id="element-3642" data-mp="r36162lq5fvm">{isEs ? 'Otro' : 'Other'}</option>
                      </select>
                    </div>

                    <div data-id="element-3643" data-mp="w295tlxslk32">
                      <label className="block font-nunito font-semibold text-sm text-gray-700 mb-1.5" data-id="element-3644" data-mp="w4ll1rc61gcf">
                        {isEs ? 'Breve Descripción' : 'Brief Description'}
                      </label>
                      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder={isEs ? '¿Por qué debería destacarse a este creador?' : 'Why should this creator be featured?'} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-haa-blue focus:ring-2 focus:ring-haa-blue/20 outline-none transition-all font-nunito text-sm resize-none" data-id="element-3645" data-mp="2o24wfx46vayb" />
                    </div>
                  </div>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3" data-id="element-3646" data-mp="1rab5ow45iyfc">
                {submitted ? <button onClick={() => {
              setModalOpen(false);
              resetForm();
            }} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3647" data-mp="bsevqovzuod9">
                    {isEs ? 'Cerrar' : 'Close'}
                  </button> : <>
                    <button onClick={() => setModalOpen(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-100 font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-3648" data-mp="uhl5hmet85mx">
                      {isEs ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button onClick={handleSubmit} className="flex-1 py-2.5 bg-gradient-to-r from-haa-blue to-haa-navy hover:from-haa-midblue hover:to-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-all" data-id="element-3649" data-mp="35axqn1jyxdkv">
                      {isEs ? 'Enviar' : 'Submit'}
                    </button>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}